import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Form, Button, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap-5';
import { connect } from 'react-redux';

import SuggestedListingForm from './suggested_listing_form';
import { countryListPropType } from './prop_types';
import { post } from 'helpers/api';
import { DeleteReviewConfirmModal } from './review_listing_details_modal/delete_review_confirm_modal';
import { FailedDocumentsAlert } from './review_listing_details_modal/failed_documents_alert';
import { ActionRequiredSection, AccuracySection } from './review_listing_details_modal/form_sections';
import {
  MODAL_STEPS,
  SUBMIT_ACTIONS,
  parseNumericValue,
  buildInitialFormState,
  computeActionRequiredFields,
  findMatchingBroker,
  buildFullAddress,
} from './review_listing_details_modal/helpers';

export function ReviewListingDetailsModal({
  show,
  countryList,
  defaultBrokerId,
  hasInsights,
  maxBrokersLimit,
  mergeResult,
  saving,
  saveError,
  showCreateDocButton,
  brokerSelectOptions,
  propertyTypes,
  onConfirm,
  onDelete,
  onHide,
}) {
  const [formState, setFormState] = useState(() => buildInitialFormState(mergeResult, countryList));
  const [validationTriggered, setValidationTriggered] = useState(false);
  const [activeSubmitAction, setActiveSubmitAction] = useState(null);
  const [modalStatus, setModalStatus] = useState(
    hasInsights ? MODAL_STEPS.NEW_LISTING : MODAL_STEPS.LISTING_NOT_FOUND
  );
  const [fullAddress, setFullAddress] = useState('');
  const [suggestedAddresses, setSuggestedAddresses] = useState([]);
  const [checkingInsights, setCheckingInsights] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [brokers, setBrokers] = useState(() => {
    const match = findMatchingBroker(brokerSelectOptions, mergeResult?.mergedFormData?.broker_name);
    return match ? [match] : [{}];
  });

  // Snapshot of which fields the AI left blank, taken once from the initial extraction. Section
  // placement stays fixed for the modal's lifetime so filling a field doesn't relocate its input.
  const [actionRequiredFields] = useState(() => computeActionRequiredFields({ formState, brokers, defaultBrokerId }));

  const isNewListing = modalStatus === MODAL_STEPS.NEW_LISTING;
  const isSuggestedListing = modalStatus === MODAL_STEPS.SUGGESTED_LISTING;

  const populatedFields = new Set(mergeResult?.populatedFieldNames || []);
  const inferredFields = new Set(mergeResult?.inferredFields || []);

  /* eslint-disable camelcase */
  const {
    address, city, state, zip, county, country,
    sale_or_lease, name, location_description, property_type_id,
    building_size, building_class, sale_price,
    lot_size, number_of_units, zoning, year_built, year_renovated, occupancy,
    generate_sale_description, generate_sale_bullets,
    generate_lease_description, generate_lease_bullets,
  } = formState;
  /* eslint-enable camelcase */

  // Live view of which required fields are still blank — drives validation feedback, the
  // warning icon, and submit gating, so it clears as the user fills each field.
  const unresolvedRequiredFields = computeActionRequiredFields({ formState, brokers, defaultBrokerId });
  const isAddressInvalid = unresolvedRequiredFields.has('address');
  const isCityInvalid = unresolvedRequiredFields.has('city');
  const isCountryInvalid = unresolvedRequiredFields.has('country_id');
  const isStateInvalid = unresolvedRequiredFields.has('state');
  const isZipInvalid = unresolvedRequiredFields.has('zip');
  const isPropertyTypeInvalid = unresolvedRequiredFields.has('property_type_id');
  const isBrokersInvalid = unresolvedRequiredFields.has('broker_name');
  const isSaleOrLeaseInvalid = unresolvedRequiredFields.has('sale_or_lease');

  const isFormInvalid = unresolvedRequiredFields.size > 0;

  const isSearchOrCreatePending =
    (saving || checkingInsights) && activeSubmitAction === SUBMIT_ACTIONS.SEARCH_OR_CREATE;
  const isViewProposalPending = saving && activeSubmitAction === SUBMIT_ACTIONS.VIEW_PROPOSAL;

  /* eslint-disable camelcase */
  const researchPropertyParams = {
    address, 
    city,
    state, 
    zip, 
    county,
    country_id: country?.id,
    property_type_id,
    building_size,
    building_class,
    lot_size,
    number_of_units,
    zoning, 
    year_built,
    renovated: year_renovated, 
    occupancy_pct: occupancy,
  };

  const propertyParams = {
    sale_checked: sale_or_lease === 'sale',
    lease_checked: sale_or_lease === 'lease',
    name,
    location_description,
    financial_statements_attributes: sale_price
      ? { '0': { sale_price: parseNumericValue(sale_price) } }
      : undefined,
  };

  const aiContentFlags = {
    generateSaleDescription: generate_sale_description,
    generateSaleBullets: generate_sale_bullets,
    generateLeaseDescription: generate_lease_description,
    generateLeaseBullets: generate_lease_bullets,
  };
  /* eslint-enable camelcase */

  // ── Field helpers ─────────────────────────────────────────────────────────

  const handleChange = patch => setFormState(prev => ({ ...prev, ...patch }));

  const handleCountryChange = countryObj => handleChange({
    country: countryObj,
    state: countryObj.countryStates?.[0]?.code || '',
    zip: '',
  });

  // ── Search / submit handlers ──────────────────────────────────────────────

  const confirmCreate = (extra = {}) => onConfirm({
    propertyParams, researchPropertyParams, brokerIds: brokers.map(b => b.value), aiContentFlags, ...extra,
  });

  const handlePropertySearch = async () => {
    setCheckingInsights(true);
    const body = {
      insights_property: { address, city, state, zip },
      only: ['address', 'city', 'state', 'zip', 'propertyId'],
    };
    const insightResponse = await post('/insights_properties/search', body);
    const builtAddress = buildFullAddress({ address, city, state, zip });
    setFullAddress(builtAddress);

    if (insightResponse['total'] === 0) {
      setModalStatus(MODAL_STEPS.LISTING_NOT_FOUND);
      confirmCreate({ createDocument: false });
    } else {
      const records = insightResponse['hits'].map(record => ({
        fullAddress: buildFullAddress(record),
        id: record.propertyId,
      }));
      setSuggestedAddresses(records);
      setModalStatus(MODAL_STEPS.SUGGESTED_LISTING);
    }
    setCheckingInsights(false);
  };

  const suggestedPropertyListing = params => confirmCreate({ createDocument: false, ...params });

  const returnFromAddressCorrection = () => setModalStatus(MODAL_STEPS.NEW_LISTING);

  const handleSearchOrCreate = () => {
    setValidationTriggered(true);
    setActiveSubmitAction(SUBMIT_ACTIONS.SEARCH_OR_CREATE);
    if (isFormInvalid) return;
    if (isNewListing) {
      handlePropertySearch();
    } else {
      confirmCreate({ createDocument: false });
    }
  };

  const handleViewProposal = () => {
    setValidationTriggered(true);
    setActiveSubmitAction(SUBMIT_ACTIONS.VIEW_PROPOSAL);
    if (isFormInvalid) return;
    if (isSaleOrLeaseInvalid) return;
    confirmCreate({ createDocument: true });
  };

  // ── Render ────────────────────────────────────────────────────────────────

  if (!mergeResult) return null;

  const isBusy = saving || checkingInsights;

  const withRequiredFieldsTooltip = button => (isFormInvalid ? (
    <OverlayTrigger
      overlay={<Tooltip id="required-fields-missing">Complete the required fields above to continue</Tooltip>}
      placement="top"
    >
      <span className="d-inline-block">{button}</span>
    </OverlayTrigger>
  ) : button);

  const fieldCtx = {
    formState,
    countryList,
    propertyTypes,
    defaultBrokerId,
    maxBrokersLimit,
    brokers,
    setBrokers,
    handleChange,
    handleCountryChange,
    validationTriggered,
    isAddressInvalid,
    isCityInvalid,
    isCountryInvalid,
    isStateInvalid,
    isZipInvalid,
    isPropertyTypeInvalid,
    isBrokersInvalid,
    isSaleOrLeaseInvalid,
    inferredFields,
    populatedFields,
    actionRequiredFields,
    unresolvedRequiredFields,
  };

  return (
    <>
      <Modal dialogClassName="modal-lg" scrollable show={show && !showDeleteConfirm} onHide={onHide}>
        <Modal.Header closeButton>
          <div>
            <Modal.Title>
              Review Listing Details
            </Modal.Title>
          </div>
        </Modal.Header>

        <Modal.Body className="p-3">
          {isSuggestedListing ? (
            <SuggestedListingForm
              backButtonClicked={returnFromAddressCorrection}
              brokers={brokers}
              errorMessages={saveError ? { base: [saveError] } : {}}
              fullAddress={fullAddress}
              hasErrors={!!saveError}
              maxBrokersLimit={maxBrokersLimit}
              saving={saving}
              showBrokerSelect={!defaultBrokerId}
              suggestedAddresses={suggestedAddresses}
              suggestedPropertyListing={suggestedPropertyListing}
              userEnteredFields={researchPropertyParams}
              onBrokerChange={setBrokers}
            />
          ) : (
            <>
              {saveError && (
                <Alert variant="danger">{saveError}</Alert>
              )}

              <FailedDocumentsAlert documents={mergeResult?.failedDocuments} />

              <Form autoComplete="off" className="form-vertical-field-container" noValidate>
                <ActionRequiredSection ctx={fieldCtx} />
                <AccuracySection ctx={fieldCtx} />
              </Form>
            </>
          )}
        </Modal.Body>

        {!isSuggestedListing && (
          <Modal.Footer>
            <div className="w-100 d-flex justify-content-between align-items-center">
              {onDelete && mergeResult?.ingestReviewId ? (
                <Button
                  disabled={isBusy}
                  variant="outline-danger"
                  onClick={() => setShowDeleteConfirm(true)}
                >
                  Delete
                </Button>
              ) : <span />}

              <div className="d-flex gap-2">
                {showCreateDocButton && withRequiredFieldsTooltip(
                  <Button
                    disabled={isBusy || isFormInvalid}
                    style={isFormInvalid ? { pointerEvents: 'none' } : undefined}
                    variant="secondary"
                    onClick={handleViewProposal}
                  >
                    {isViewProposalPending ? 'Creating…' : 'View Proposal'}
                  </Button>
                )}
                {withRequiredFieldsTooltip(
                  <Button
                    disabled={isBusy || isFormInvalid}
                    style={isFormInvalid ? { pointerEvents: 'none' } : undefined}
                    variant="primary"
                    onClick={handleSearchOrCreate}
                  >
                    {isSearchOrCreatePending ? 'Loading…' : 'View Property Details'}
                  </Button>
                )}
              </div>
            </div>
          </Modal.Footer>
        )}
      </Modal>

      <DeleteReviewConfirmModal
        show={showDeleteConfirm}
        onCancel={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          setShowDeleteConfirm(false);
          onDelete();
        }}
      />
    </>
  );
}

ReviewListingDetailsModal.propTypes = {
  brokerSelectOptions: PropTypes.array,
  countryList: countryListPropType.isRequired,
  defaultBrokerId: PropTypes.number,
  hasInsights: PropTypes.bool.isRequired,
  maxBrokersLimit: PropTypes.number.isRequired,
  mergeResult: PropTypes.shape({
    allIngestMetadata: PropTypes.array,
    conflicts: PropTypes.array,
    failedDocuments: PropTypes.arrayOf(PropTypes.shape({
      uploadId: PropTypes.number.isRequired,
      filename: PropTypes.string,
      errorMessage: PropTypes.string.isRequired,
    })),
    inferredFields: PropTypes.arrayOf(PropTypes.string),
    ingestReviewId: PropTypes.number,
    mergedFormData: PropTypes.object,
    populatedFieldNames: PropTypes.arrayOf(PropTypes.string),
  }),
  propertyTypes: PropTypes.objectOf(PropTypes.string),
  saveError: PropTypes.string,
  saving: PropTypes.bool,
  show: PropTypes.bool.isRequired,
  showCreateDocButton: PropTypes.bool,
  onConfirm: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onHide: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  brokerSelectOptions: state.brokerSelectOptions || [],
  propertyTypes: state.propertyTypes || {},
});

export default connect(mapStateToProps)(ReviewListingDetailsModal);
